/**
 * Pending Request Banner Component
 *
 * Displays a countdown timer for pending pairing requests that expire after 10 minutes.
 * Shows user-friendly message prompting them to check their email for verification link.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface PendingRequestBannerProps {
  /** ISO 8601 expiry timestamp */
  expiresAt: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Calculate minutes remaining until expiry
 */
function calculateMinutesRemaining(expiresAt: string): number {
  const now = new Date().getTime();
  const expiry = new Date(expiresAt).getTime();
  const diff = expiry - now;
  return Math.max(0, Math.ceil(diff / (60 * 1000)));
}

/**
 * PendingRequestBanner Component
 *
 * Info banner with countdown timer for pending pairing requests
 */
export const PendingRequestBanner = React.forwardRef<HTMLDivElement, PendingRequestBannerProps>(
  ({ expiresAt, className }, ref) => {
    const { t } = useTranslation();
    const [minutesRemaining, setMinutesRemaining] = React.useState(
      calculateMinutesRemaining(expiresAt)
    );

    // Update countdown every minute
    React.useEffect(() => {
      const interval = setInterval(() => {
        setMinutesRemaining(calculateMinutesRemaining(expiresAt));
      }, 60000); // Update every 60 seconds

      return () => clearInterval(interval);
    }, [expiresAt]);

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start gap-[0.625rem] rounded-[0.3125rem] bg-blue-50 border border-blue-200',
          'px-4 py-3 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <svg
          className="w-5 h-5 shrink-0 mt-[0.125rem]"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
        <div className="flex-1">
          <p className="text-[0.9375rem] font-medium">
            {t('pairing.banners.checkEmail', { minutes: minutesRemaining })}
          </p>
        </div>
      </div>
    );
  }
);

PendingRequestBanner.displayName = 'PendingRequestBanner';
